import i18next from "i18next";
/**
 * Tiny formatting helpers reused across the UI. Pure functions so they
 * can be exercised by Vitest without rendering a single React tree.
 */

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Coarse "X minutes ago" / "in X hours" formatter. Unit thresholds are
 * picked so every visible row in the notification panel collapses to a
 * stable string at a glance instead of a precise wall-clock time.
 *
 * Purposely English-only — the bell drops back to {@code locale-aware}
 * `Date.toLocaleString` for older items, so this helper only has to
 * cover the last 24 h of activity.
 */
export function formatRelative(input: Date | string | number, now: Date = new Date()): string {
  const t = input instanceof Date ? input.getTime() : new Date(input).getTime();
  if (Number.isNaN(t)) return "";
  const delta = now.getTime() - t;
  const abs = Math.abs(delta);
  const past = delta >= 0;

  const fmt = (n: number, unit: string) => {
    const plural = n === 1 ? unit : unit + "s";
    return past ? `${n} ${plural} ago` : `in ${n} ${plural}`;
  };

  if (abs < 45 * SECOND) return past ? "just now" : "in a moment";
  if (abs < HOUR) return fmt(Math.round(abs / MINUTE), "minute");
  if (abs < DAY) return fmt(Math.round(abs / HOUR), "hour");
  return fmt(Math.round(abs / DAY), "day");
}

/**
 * Human-readable file size in IEC binary units. Used by the materials
 * list and the audit-log download size column.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KiB", "MiB", "GiB", "TiB"];
  let size = bytes / 1024;
  let unitIdx = 0;
  while (size >= 1024 && unitIdx < units.length - 1) {
    size /= 1024;
    unitIdx++;
  }
  return `${size.toFixed(size < 10 ? 1 : 0)} ${units[unitIdx]}`;
}

/**
 * Truncates a string to {@code max} characters and appends an ellipsis
 * when it had to cut. Preserves the original when it already fits so
 * callers can compare lengths to detect truncation.
 */
export function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 1)) + "…";
}

/**
 * Jezik u obliku koji `Intl` razume, iz oznake koju portal koristi.
 *
 * Srpski se u `Intl` podrazumevano piše ćirilicom (`sr` → `sr-Cyrl`), a ceo portal je
 * latinični. Bez ovog preslikavanja usred latiničnog ekrana osvane „22. сеп".
 *
 * Isti razlog važi i obrnuto: poziv bez jezika uzima jezik pregledača, pa se srpskom
 * korisniku na američkom računaru datum ispiše kao `9/22/2026`.
 */
export function intlLocale(appLocale?: string | null): string {
  const l = (appLocale ?? tekuciJezik()).toLowerCase();
  if (l === "sr" || l.startsWith("sr-") || l === "sr-latn") return "sr-Latn";
  return appLocale ?? "sr";
}

/**
 * Jezik koji je trenutno izabran u portalu.
 *
 * Pomoćnik ga čita sam umesto da se provlači kroz svaku ugnježdenu komponentu. Datum se
 * ispisuje na desetinama mesta, a većina njih nema `useTranslation` u svom opsegu; kad se
 * jezik mora proslediti, jedno mesto ga zaboravi i tamo osvane američki format.
 */
function tekuciJezik(): string {
  return i18next.resolvedLanguage ?? i18next.language ?? "sr";
}

/** Datum: 22.09.2026. Prazno za prazan ulaz, da pozivaoci ne pišu istu proveru svuda. */
export function formatDate(iso: string | Date | null | undefined, appLocale?: string): string {
  if (!iso) return "";
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(intlLocale(appLocale));
}

/** Datum sa vremenom: 22.09.2026. 14:05. */
export function formatDateTime(iso: string | Date | null | undefined, appLocale?: string): string {
  if (!iso) return "";
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(intlLocale(appLocale));
}

/** Kratak datum za kartice i uske kolone: 22. sep. */
export function formatDateShort(iso: string | Date | null | undefined, appLocale?: string): string {
  if (!iso) return "";
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(intlLocale(appLocale), { day: "numeric", month: "short" });
}

/**
 * Iznos u srpskom zapisu: 14.722.425,00 €. Tačka deli hiljade, zarez decimale,
 * oznaka valute stoji iza broja.
 *
 * Isti razlog kao kod datuma: `Intl` bez jezika uzima jezik pregledača, pa se srpskom
 * korisniku na američkom računaru ispiše „€14,722,425.00" — zapis koji se ovde ne koristi
 * i koji se čita naopako, jer je tačka tamo decimalna.
 */
export function formatMoney(
  n: number | null | undefined,
  currency?: string | null,
  appLocale?: string,
  opts?: Intl.NumberFormatOptions,
): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(intlLocale(appLocale), {
    style: "currency",
    currency: currency || "EUR",
    ...opts,
  }).format(n);
}

/**
 * A unit price, with up to four decimals. The ERP keeps a price to four decimals and prices below
 * one euro use them: 0.505 shown as 0.51 makes 2,000 × price read as 1,020.00 next to a line amount
 * of 1,010.00. Amounts keep two decimals ({@link formatMoney}); only the price shows more.
 */
export function formatUnitPrice(
  n: number | null | undefined,
  currency?: string | null,
  appLocale?: string,
): string {
  return formatMoney(n, currency, appLocale, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

/**
 * Isti iznos, ali sa šifrom valute umesto znaka: „14.722.425 RSD".
 * Koristi se gde stoje iznosi u više valuta jedan uz drugi, jer „US$" pored „RSD"
 * izgleda kao greška u podatku.
 */
export function formatMoneyCode(
  n: number | null | undefined,
  currency?: string | null,
  appLocale?: string,
  opts?: Intl.NumberFormatOptions,
): string {
  return formatMoney(n, currency, appLocale, { currencyDisplay: "code", ...opts });
}

/** Broj bez valute, sa srpskim razdvajanjem hiljada: 14.722.425. */
export function formatNumber(
  n: number | null | undefined,
  appLocale?: string,
  opts?: Intl.NumberFormatOptions,
): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(intlLocale(appLocale), opts).format(n);
}
